import { Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import React, { useMemo } from "react";

import { activityApi } from "../../../../apis";
import { getMetricsByCategory } from "../../../../constants/metrics";
import {
  MetricValueAggregatedResponseDto,
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
} from "../../../../libs/cellera-sdk";
import MetricCard, { MetricData } from "./MetricCard";
import SectionWrapper from "./SectionWrapper";

// Get sleep metrics from centralized configuration
const SLEEP_METRICS_MAP = getMetricsByCategory("sleep");

type SleepMetricCode = keyof typeof SLEEP_METRICS_MAP;

interface SleepProps {
  patientId: string;
  onViewHistory: () => void;
}

// Helper function to format minutes into hours and minutes
const formatMinutesToHoursMinutes = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  const hourText = hours === 1 ? "hour" : "hours";
  const minuteText = minutes === 1 ? "minute" : "minutes";
  return `${hours} ${hourText} ${minutes} ${minuteText}`;
};

const formatValue = (value: unknown, metricCode?: string): string => {
  if (value === null || value === undefined) return "No Data";

  // Handle sleep time metrics first (even if they come as numbers)
  if (
    (metricCode === MetricValuesAggregationRequestDtoMetricCodeEnum.TimeInBed ||
      metricCode ===
        MetricValuesAggregationRequestDtoMetricCodeEnum.SleepTime ||
      metricCode ===
        MetricValuesAggregationRequestDtoMetricCodeEnum.ActualSleepTime) &&
    typeof value === "number"
  ) {
    return formatMinutesToHoursMinutes(value);
  }

  // Handle numeric values
  if (typeof value === "number") {
    return Math.round(value).toString();
  }

  // Handle sleep data (might be JSON format with sessions and time)
  if (typeof value === "object" && value !== null) {
    const sleep = value as {
      sessions?: number;
      totalTime?: number;
      rem?: number;
      stage1?: number;
      stage2?: number;
      stage3?: number;
      remSleep?: number;
      awakeTime?: number;
      deepSleep?: number;
      lightSleep?: number;
      sleepEfficiency?: number;
    };

    // Handle sleep analysis data
    if (
      metricCode ===
      MetricValuesAggregationRequestDtoMetricCodeEnum.SleepAnalysis
    ) {
      const { remSleep = 0, deepSleep = 0, lightSleep = 0 } = sleep;
      const totalSleepMinutes = remSleep + deepSleep + lightSleep;
      return formatMinutesToHoursMinutes(totalSleepMinutes);
    }

    // Handle sleep stages data
    if (
      metricCode === MetricValuesAggregationRequestDtoMetricCodeEnum.SleepStages
    ) {
      const { rem = 0, stage1 = 0, stage2 = 0, stage3 = 0 } = sleep;
      const totalMinutes = rem + stage1 + stage2 + stage3;
      return formatMinutesToHoursMinutes(totalMinutes);
    }

    // Handle time in bed data
    if (
      metricCode === MetricValuesAggregationRequestDtoMetricCodeEnum.TimeInBed
    ) {
      const { totalTime = 0 } = sleep;
      return formatMinutesToHoursMinutes(totalTime);
    }

    // Handle other sleep data
    if (typeof sleep.sessions === "number") {
      return sleep.sessions.toString();
    }

    if (typeof sleep.totalTime === "number") {
      return formatMinutesToHoursMinutes(sleep.totalTime);
    }

    return "No Data";
  }

  return "No Data";
};

const getFallbackInfo = (): MetricData["fallbackInfo"] => ({
  message: "No sleep data available",
  suggestion: "Sync with health app or manual entry",
  lastRecorded: "Not available",
});

// Static sleep queries configuration - moved outside component for better performance
const sleepQueries = [
  {
    queryKey: "sleep_analysis",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.SleepAnalysis,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "time_in_bed",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.TimeInBed,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "sleep_session_events",
    metricCode:
      MetricValuesAggregationRequestDtoMetricCodeEnum.SleepSessionEvents,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "sleep_stages",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.SleepStages,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "sleep_time",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.SleepTime,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
  {
    queryKey: "actual_sleep_time",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.ActualSleepTime,
    aggregateType: MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
  },
];
const Sleep: React.FC<SleepProps> = ({ patientId, onViewHistory }) => {
  // Memoize today's date range to avoid recreating on every render
  const dateRange = useMemo(() => {
    const today = new Date();
    const startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return {
      startDate: startOfDay.toISOString(),
      endDate: endOfDay.toISOString(),
    };
  }, []);

  // Memoize query configuration to prevent recreation
  const queryConfig = useMemo(
    () => ({
      enabled: !!patientId,
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }),
    [patientId]
  );

  const [
    { data: sleepAnalysisData, isLoading: sleepAnalysisLoading },
    { data: timeInBedData, isLoading: timeInBedLoading },
    { data: sleepSessionEventsData, isLoading: sleepSessionEventsLoading },
    { data: sleepStagesData, isLoading: sleepStagesLoading },
    { data: sleepTimeData, isLoading: sleepTimeLoading },
    { data: actualSleepTimeData, isLoading: actualSleepTimeLoading },
  ] = useQueries({
    queries: sleepQueries.map((query) => ({
      queryKey: ["sleep", patientId, query.queryKey],
      queryFn: () => {
        if (!patientId) return null;

        const request: MetricValuesAggregationRequestDto = {
          metricCode: query.metricCode,
          healthProviderCode:
            MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
          interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
          aggregateTypes: [query.aggregateType],
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          orderBy: "DESC",
        };

        return activityApi.getMetricValuesAggregatedByPatientId({
          patientId,
          metricValuesAggregationRequestDto: request,
        });
      },
      ...queryConfig,
    })),
  });

  const metricDataMap = useMemo(
    (): Record<string, MetricValueAggregatedResponseDto | undefined> => ({
      [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepAnalysis]:
        sleepAnalysisData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.TimeInBed]:
        timeInBedData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepSessionEvents]:
        sleepSessionEventsData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepStages]:
        sleepStagesData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.SleepTime]:
        sleepTimeData?.data[0],
      [MetricValuesAggregationRequestDtoMetricCodeEnum.ActualSleepTime]:
        actualSleepTimeData?.data[0],
    }),
    [
      sleepAnalysisData,
      timeInBedData,
      sleepSessionEventsData,
      sleepStagesData,
      sleepTimeData,
      actualSleepTimeData,
    ]
  );

  // Memoize display data to prevent recalculation on every render
  const displayData = useMemo(
    (): MetricData[] =>
      Object.keys(SLEEP_METRICS_MAP).map((metricCode) => {
        const metricInfo = SLEEP_METRICS_MAP[metricCode as SleepMetricCode];
        const latestValue = metricDataMap[metricCode];

        if (!latestValue) {
          return {
            id: metricCode,
            name: metricInfo.name,
            value: "No Data",
            unit: metricInfo.unit,
            lastChecked: "Never",
            hasData: false,
            fallbackInfo: getFallbackInfo(),
          };
        }

        const formattedValue = formatValue(
          latestValue.valueJsonData?.sum ??
            latestValue.valueIntData?.sum ??
            latestValue.valueFloatData?.sum,
          metricCode
        );

        const isNoData = formattedValue === "No Data" || formattedValue === "0";

        return {
          id: metricCode,
          name: metricInfo.name,
          value: formattedValue,
          unit: metricInfo.unit,
          lastChecked: new Date(latestValue.startDate).toLocaleString("vi-VN"),
          hasData: !isNoData,
          fallbackInfo: isNoData ? getFallbackInfo() : undefined,
        };
      }),
    [metricDataMap]
  );

  // Memoize loading state calculation
  const isLoading = useMemo(
    () =>
      sleepAnalysisLoading ||
      timeInBedLoading ||
      sleepSessionEventsLoading ||
      sleepStagesLoading ||
      sleepTimeLoading ||
      actualSleepTimeLoading,
    [
      sleepAnalysisLoading,
      timeInBedLoading,
      sleepSessionEventsLoading,
      sleepStagesLoading,
      sleepTimeLoading,
      actualSleepTimeLoading,
    ]
  );

  if (isLoading) {
    return (
      <SectionWrapper title="Sleep" onViewAll={onViewHistory}>
        <Typography>Loading sleep data...</Typography>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper title="Sleep" onViewAll={onViewHistory} spacing="compact">
      {displayData.map((metric) => (
        <MetricCard key={metric.id} metric={metric} />
      ))}
    </SectionWrapper>
  );
};

export default Sleep;
